import { errorMessage, type SiteChoiceOption } from "@lumora/bridge";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { client } from "../client.js";
import { attemptSignIn } from "./signInFlow.js";

/** Remembered slug for display only — login resolves tenant from email (#458). */
export const LAST_SITE_KEY = "lumora.lastSiteSlug";

function rememberSite(slug: string | undefined): void {
  if (!slug) return;
  try {
    localStorage.setItem(LAST_SITE_KEY, slug);
  } catch {
    /* display-only hint; ignore storage failures */
  }
}

export function SignInPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  // Held only in component state for the "Choose a site" re-submit; never stored or logged.
  const [password, setPassword] = useState("");
  const [sites, setSites] = useState<SiteChoiceOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(site?: string) {
    setLoading(true);
    setError(null);
    try {
      const res = await attemptSignIn(client, { email, password, ...(site ? { site } : {}) });
      if (res.kind === "choose") {
        setSites(res.sites);
        return;
      }
      rememberSite(res.site);
      setPassword("");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setPassword("");
      setSites(null);
      setError(errorMessage(err, "Sign-in failed. Please try again."));
    } finally {
      setLoading(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  const choosing = sites !== null;

  return (
    <div className="site-signin">
      <p className="site-signin-kicker">Site owners</p>
      <h1>{choosing ? "Choose a site" : "Sign in"}</h1>
      <p className="site-signin-lead">
        {choosing ? (
          "This password works for more than one site. Pick the one to open."
        ) : (
          <>
            Use the email and password for your site account. New business?{" "}
            <Link to="/get-started">Get started</Link>.
          </>
        )}
      </p>

      <section data-lumora-view="sign-in">
        {choosing ? (
          <div className="site-signin-choices" role="group" aria-label="Choose a site">
            {error ? <p data-lumora-error>{error}</p> : null}
            {sites.map((s) => (
              <button
                key={s.slug}
                type="button"
                className="site-signin-choice"
                disabled={loading}
                onClick={() => void submit(s.slug)}
              >
                {s.name || s.slug}
                <span className="muted"> {s.slug}</span>
              </button>
            ))}
            <button
              type="button"
              className="site-signin-change"
              disabled={loading}
              onClick={() => {
                setSites(null);
                setPassword("");
                setError(null);
              }}
            >
              Back
            </button>
          </div>
        ) : (
          <form aria-label="Sign in" onSubmit={onSubmit}>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                disabled={loading}
              />
            </label>
            <label>
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                }}
                disabled={loading}
              />
            </label>
            {error ? <p data-lumora-error>{error}</p> : null}
            <button type="submit" disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}
      </section>
      <p className="muted site-signin-foot">
        Lumora staff? <Link to="/platform/signin">Platform admin</Link>
      </p>
    </div>
  );
}
